const _ = require('lodash');
const { DATE_FORMATS, PER_WEEK_DATE_FORMATS, NUMBER_FORMAT_LOCALES } = require('../constants');

// In "priority order", from first one to check
const Types = [
	'object', // The current report for example
	'user',
	'publisher',
	'global',
];

// All "effective user settings that exists"
const AvailSettings = {
	dateFormat: {
		type: String,
		defaultValue: 'YYYY-MM-DD',
		enum: [null, ...DATE_FORMATS.map((d) => d.value)],
		selfCanModify: true,
	},
	perWeekDateFormat: {
		type: String,
		defaultValue: 'GGGG [Week] W',
		enum: [null, ...PER_WEEK_DATE_FORMATS.map((d) => d.value)],
		selfCanModify: true,
	},
	numberFormatLocale: {
		type: String,
		defaultValue: 'en-GB',
		enum: [null, ...NUMBER_FORMAT_LOCALES.map((d) => d.value)],
		selfCanModify: true,
	},
	timezone: { type: String, defaultValue: 'UTC', selfCanModify: true },
	currency: { type: String, defaultValue: 'EUR', selfCanModify: true },
	limitToPortfolio: {
		type: Boolean,
		defaultValue: false,
		enum: [null, true, false],
		selfCanModify: false,
		get(v) {
			return this.parent?.()?.isAdministrator ? false : v;
		},
	},
};

const AvailKeys = _.keys(AvailSettings);

const arrangedEntities = (entities) => _.orderBy(_.filter(entities, 'obj'), [
	(e) => Types.indexOf(e.type),
	(e) => -(e.priority || 0),
]);

const withEntities = (orgEntities, entities) => {
	const types = _.map(entities, 'type');
	const toKeep = orgEntities.filter(({ type }) => !types.includes(type));
	return arrangedEntities([...toKeep, ...entities]);
};

const toObj = (obj) => (_.isFunction(obj) ? obj() : obj);

/** Class used for obtaining settings in the user settings hierarchy. That "hierarchy" currently
 * consists of the levels:
 *
 * [Report or other Object] => User => Publisher => Global => (default values)
 *
 * If the value is null or undefined on one level, it checks the next, etc. until it uses the
 * default value (.defaultValue in AvailableSettings)
 */
class EffectiveUserSettings {
	static AvailSettings = AvailSettings;

	constructor(entities = []) {
		this.setEntities(entities);
	}

	static dbSchema() {
		return _.mapValues(AvailSettings, (v) => _.omit(v, ['defaultValue', 'selfCanModify']));
	}

	/** The "current" settings. This is the default implementation which only gives the default values.
	 * Will be overwritten in frontend / backend. */
	static current() {
		return new EffectiveUserSettings();
	}

	/** Returns a version of the current() settings that only contains 'global' settings. */
	static global() {
		return new EffectiveUserSettings(_.filter(EffectiveUserSettings.current().entities, { type: 'global' }));
	}

	setEntities(entities) {
		this.entities = arrangedEntities(entities);
	}

	// Adds/replaces/removes objects from the chain, runs 'fn' and restores the chain of settings objects (.entities)
	async with(entities, fn) {
		const prev = this.entities;
		try {
			this.entities = withEntities(this.entities, entities);
			return await fn();
		} finally {
			this.entities = prev;
		}
	}

	// Returns a new object from this one with settings objects added/replaced/removed according to 'entities'
	settingsWith(entities) {
		return new EffectiveUserSettings(withEntities(this.entities, entities));
	}

	// Helper so that the later functions behaves differently depending on if 'fn' is provided
	callWith(entities, fn) {
		return this[fn ? 'with' : 'settingsWith'](entities, fn);
	}

	// Removes a type, for example: 'user'
	withoutType(type, fn) {
		return this.callWith([{ type }], fn);
	}

	// Adds/replaces the settings object for type 'object'
	withObject(obj, fn) {
		return this.callWith([{ type: 'object', obj }], fn);
	}

	// All settings "below" level 'type' is skipped. Example type === 'publisher' only keeps settings objects
	// for levels ['publisher', 'global']
	atLevel(type, fn) {
		return this.callWith(Types.slice(0, Types.indexOf(type)).map((t) => ({ type: t })), fn);
	}

	// All settings "below" *or at* level 'type' is skipped. Example type === 'publisher' only keeps settings objects
	// for levels ['global']
	aboveLevel(type, fn) {
		const levelType = Types[Types.indexOf(type) + 1];
		if (levelType) {
			return this.atLevel(levelType, fn);
		} else { // type is 'global', all entitites should be removed
			return this.callWith(Types.map((t) => ({ type: t })), fn);
		}
	}

	// Returns the first settings object, if any, of type 'type'.
	// Currently, there will only be one settings object for each type.
	getObjectByType(type) {
		return toObj(_.find(this.entities, { type })?.obj);
	}

	// Returns an "effective" value for a field 'fld' such as 'currency'
	get(fld) {
		const setting = AvailSettings[fld];
		if (!setting) {
			throw Error(`Unknown user setting '${fld}'`);
		}
		const objects = this.entities.map(({ obj }) => toObj(obj));
		// "!=" below to check both null + undefined
		return objects.find((o) => o?.[fld] != null)?.[fld] || setting.defaultValue;
	}

	// Returns an object with all "effective" values
	all() {
		return _.mapValues(AvailSettings, (__, fld) => this.get(fld));
	}

	// "Serializes" the settings objects (.entities) for sending backend => frontend
	clientEntites(excludeTypes = ['object']) {
		return this.entities.filter((e) => !excludeTypes.includes(e.type)).map(({ type, obj }) => {
			const raw = _.pick(toObj(obj), AvailKeys);
			return { type, obj: raw };
		});
	}
}

// Create getters, for example: .get('currency') => .currency
_.keys(AvailSettings).forEach((key) => {
	Object.defineProperty(EffectiveUserSettings.prototype, key, {
		get() {
			return this.get(key);
		},
		enumerable: true,
	});
});

module.exports = EffectiveUserSettings;
